import api from "@shared/api/client";
import type { MeterProviderInfo, MeterSettingsResponse } from "@shared/types/meter";
import { MeterSubmissionKind } from "@shared/types/meter";
import { extractErrorMessage, findMissingMeterSettings } from "@shared/utils";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MeterSettingsFormActions, MeterSettingsFormState } from "./types";

const useMeterSettingsForm = (provider: MeterProviderInfo | null): { form: MeterSettingsFormState; actions: MeterSettingsFormActions } => {
  const { t } = useTranslation(["common", "meterReadings"]);
  const [settings, setSettings] = useState<MeterSettingsResponse | null>(null);
  const [reportName, setReportName] = useState("");
  const [reportAddress, setReportAddress] = useState("");
  const [reportCustomerCode, setReportCustomerCode] = useState("");
  const [reportPhone, setReportPhone] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [reportCc, setReportCc] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState("");

  const applySettings = useCallback((s: MeterSettingsResponse) => {
    setSettings(s);
    setReportName(s.reportName ?? "");
    setReportAddress(s.reportAddress ?? "");
    setReportCustomerCode(s.reportCustomerCode ?? "");
    setReportPhone(s.reportPhone ?? "");
    setReportEmail(s.reportEmail ?? "");
    setReportCc(s.reportCc ?? "");
  }, []);

  const loadSettings = useCallback(
    async (providerId: number) => {
      try {
        applySettings(await api.meterReadings.getSettings(providerId));
      } catch (err: unknown) {
        setError(extractErrorMessage(err, t("meterReadings:settings.loadError")));
      }
    },
    [applySettings, t],
  );

  useEffect(() => {
    if (provider) void loadSettings(provider.id);
  }, [loadSettings, provider]);

  const save = useCallback(async (): Promise<void> => {
    if (!provider) return;
    setSavingSettings(true);
    setError("");
    try {
      const usesEmail = provider.submission === MeterSubmissionKind.EMAIL;
      applySettings(
        await api.meterReadings.updateSettings(provider.id, {
          reportName: reportName.trim() || null,
          reportAddress: reportAddress.trim() || null,
          reportCustomerCode: reportCustomerCode.trim() || null,
          reportPhone: reportPhone.trim() || null,
          reportEmail: usesEmail ? reportEmail.trim() || null : null,
          reportCc: usesEmail ? reportCc.trim() || null : null,
        }),
      );
    } catch (err: unknown) {
      setError(extractErrorMessage(err, t("meterReadings:settings.saveError")));
    } finally {
      setSavingSettings(false);
    }
  }, [applySettings, provider, reportName, reportAddress, reportCustomerCode, reportPhone, reportEmail, reportCc, t]);

  const missingRequiredSettings = provider
    ? findMissingMeterSettings({ reportName, reportAddress, reportCustomerCode, reportPhone, reportEmail }, { submission: provider.submission })
    : [];

  const form: MeterSettingsFormState = {
    settings,
    reportName,
    reportAddress,
    reportCustomerCode,
    reportPhone,
    reportEmail,
    reportCc,
    savingSettings,
    error,
    settingsIncomplete: !!missingRequiredSettings.length,
  };

  const actions: MeterSettingsFormActions = {
    changeName: setReportName,
    changeAddress: setReportAddress,
    changeCustomerCode: setReportCustomerCode,
    changePhone: setReportPhone,
    changeEmail: setReportEmail,
    changeCc: setReportCc,
    save,
  };

  return { form, actions };
};

export { useMeterSettingsForm };
